import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import MuiTypography from '@mui/material/Typography';
import { format } from 'date-fns';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components/macro';

import Avatar from 'components/Avatar/Avatar';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { MessageSourcesE, MessageTypesE } from 'declarations/enums/messages';
import { TicketActionTypesE } from 'declarations/enums/tickets';
import type { TicketDetailsI } from 'declarations/interfaces/tickets';
import type { NoopT } from 'declarations/types/common';
import { getUserPlaceholderName } from 'helpers/user';

import MessageForm from './components/MessageForm/MessageForm';
import MessagesLoader from './components/MessagesLoader/MessagesLoader';
import { useMessages } from './contexts/MessagesContext';
import {
  ActionMessageHeaderStyled,
  ActionMessageSenderNameStyled,
  ActionMessageSentDateStyled,
  ActionMessageStyled,
  AttachmentNameStyled,
  CountdownStyled,
  FlexContainerStyled,
  MessageContentStyled,
  MessageOptionsWrapStyled,
  MessageSenderNameStyled,
  MessageSentDateStyled,
  MessageStyled,
  MessageWrapStyled,
  MessagesCardBottomStyled,
  MessagesCardInnerStyled,
  MessagesScrollAreaStyled,
  MessagesWrapStyled,
} from './Messages.styled';

export type MessagesPropsT = {
  roofTopName: string;
  requiresAttachment: TicketDetailsI['requiresAttachment'];
  handleOpenAddAttachmentPopup: NoopT;
  onAssignToWarrCloudChanged: (checked: boolean) => void;
};

function isScreenshotOnlyPending(text: string, file: Blob | null | undefined): boolean {
  return Boolean(file && !text.trim());
}

/** Pending screenshot with no typed message — preview cannot use empty string. */
function getUnsavedMessagePreviewText(text: string, file: Blob | null | undefined): string {
  if (isScreenshotOnlyPending(text, file)) {
    return 'Send screenshot';
  }
  return text;
}

function getConfirmPendingButtonTitle(text: string, file: Blob | null | undefined): string {
  if (isScreenshotOnlyPending(text, file)) {
    return 'Send screenshot now';
  }
  return 'Send message now';
}

function getConfirmPendingButtonAriaLabel(text: string, file: Blob | null | undefined): string {
  if (isScreenshotOnlyPending(text, file)) {
    return 'Confirm screenshot';
  }
  return 'Confirm message';
}

function getConfirmBarButtonTitle(
  isSending: boolean,
  text: string,
  file: Blob | null | undefined
): string {
  if (isSending) {
    return 'Sending…';
  }
  return getConfirmPendingButtonTitle(text, file);
}

function getConfirmBarButtonAriaLabel(
  isSending: boolean,
  text: string,
  file: Blob | null | undefined
): string {
  if (isSending) {
    return 'Sending message';
  }
  return getConfirmPendingButtonAriaLabel(text, file);
}

function getPendingSendStatusLabel(hasScreenshotFile: boolean): string {
  if (hasScreenshotFile) {
    return 'Send screenshot';
  }
  return 'Sending message…';
}

type UnsavedScreenshotThumbnailPropsT = {
  file: Blob;
};

const pendingSendStatusBarSx = {
  alignItems: 'center',
  bgcolor: 'action.hover',
  border: 1,
  borderColor: 'divider',
  borderRadius: '10px',
  display: 'flex',
  gap: 1.5,
  minHeight: 45,
  px: 2,
  py: 1,
} as const;

type PendingSendStatusBarPropsT = {
  label: string;
};

const PendingSendStatusBar: FC<PendingSendStatusBarPropsT> = ({ label }): JSX.Element => {
  return (
    <Box aria-live="polite" sx={pendingSendStatusBarSx}>
      <CircularProgress color="primary" size={24} thickness={4} />
      <MuiTypography color="text.secondary" variant="body2">
        {label}
      </MuiTypography>
    </Box>
  );
};

const UnsavedScreenshotThumbnail: FC<UnsavedScreenshotThumbnailPropsT> = ({
  file,
}): JSX.Element | null => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return (): void => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (!objectUrl) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        alignSelf: 'flex-end',
        maxWidth: '100%',
        mb: 0.5,
      }}
    >
      <Box
        alt=""
        component="img"
        src={objectUrl}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          display: 'block',
          maxHeight: 120,
          maxWidth: '100%',
        }}
      />
    </Box>
  );
};

const Messages: FC<MessagesPropsT> = ({
  roofTopName,
  requiresAttachment,
  handleOpenAddAttachmentPopup,
  onAssignToWarrCloudChanged,
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const { userProfile } = useUserProfile();
  const {
    messages,
    isCreatingMessage,
    isLoadingMessages,
    undoMessageTimeLeft,
    unsavedMessage,
    handleUndoMessage,
    handleConfirmMessage,
  } = useMessages();
  const messagesWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Hooks
   */
  const [assignToWarrCloud, setAssignToWarrCloud] = useState<boolean>(true);

  /**
   * Handlers
   */
  const handleAssignToWarrCloudChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    setAssignToWarrCloud(checked);
    onAssignToWarrCloudChanged(checked);
  };

  const handleUrlPreviewClick = (imageUrl: string): void => {
    window.open(imageUrl, '_blank', 'noopener, noreferrer');
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (messagesWrapperRef.current && (messages.length || unsavedMessage)) {
      const { current: messagesWrapper } = messagesWrapperRef;

      messagesWrapper.scrollTop = messagesWrapper.scrollHeight;
    }
  }, [messages, unsavedMessage]);

  return (
    <MessagesCardInnerStyled>
      <MessagesScrollAreaStyled ref={messagesWrapperRef}>
        <MessagesWrapStyled>
          {isLoadingMessages && messages.length === 0 ? (
            <MessagesLoader />
          ) : (
            messages.map(
              (
                {
                  action,
                  typeId,
                  sender,
                  sentDate,
                  text,
                  attachmentName = '',
                  attachmentText = '',
                  attachmentUrl = '',
                  sourceId,
                },
                i
              ) => {
                const [freeText] = text.split('{');
                const [senderFirstName, senderLastName] = sender.displayName
                  ? sender.displayName.split(' ')
                  : [];
                const isLoggedInUser = sender.userId === userProfile?.userId;

                const isTextMessage = typeId === MessageTypesE.Text;

                const isAttachmentTypeMessage =
                  typeId === MessageTypesE.ArtifactUpload ||
                  typeId === MessageTypesE.ArtifactDelete ||
                  typeId === MessageTypesE.ScreenshotUpload ||
                  typeId === MessageTypesE.AttachmentApproval;

                const isGetApprovalTypeMessage =
                  typeId === MessageTypesE.Action &&
                  action?.typeId === TicketActionTypesE.GetApproval &&
                  action?.privateMessage;

                const isPrivateMsgShown =
                  typeId === MessageTypesE.Action &&
                  action?.typeId !== TicketActionTypesE.GetApproval &&
                  action?.privateMessage;

                const isScheduleImportMessage = sourceId === MessageSourcesE.ScheduleImport;

                const isActionMessage = typeId !== MessageTypesE.Text;
                const hasThumbnail = attachmentUrl.length > 0;
                const isAssigneeChangeTypeMessage =
                  typeId === MessageTypesE.Action &&
                  action?.typeId === TicketActionTypesE.ChangeAssigneeId;

                const getJustifyValue = (): 'start' | 'end' | 'center' => {
                  if (isActionMessage) return 'center';
                  return isLoggedInUser ? 'end' : 'start';
                };

                return (
                  <MessageWrapStyled
                    data-testid="message"
                    key={`message-${i}`}
                    justify={getJustifyValue()}
                    align="start"
                  >
                    {!isActionMessage && !isLoggedInUser && (
                      <Avatar
                        size="md"
                        placeholder={getUserPlaceholderName(senderFirstName, senderLastName)}
                        imageUrl={sender.photoUrl}
                      />
                    )}
                    <FlexContainerStyled
                      direction="column"
                      align="center"
                      isLoggedInUser={isLoggedInUser}
                      hasThumbnail={hasThumbnail}
                    >
                      {isActionMessage && (
                        <ActionMessageHeaderStyled
                          direction="row"
                          align="center"
                          justify="start"
                          gap="0.25rem"
                        >
                          <ActionMessageSenderNameStyled>
                            {isScheduleImportMessage && (
                              <Typography variant="body4" color="red" isInline={true}>
                                [Import]&nbsp;
                              </Typography>
                            )}
                            <Typography
                              variant="body3"
                              isInline={true}
                              color={theme.mode === 'dark' ? 'light' : undefined}
                            >
                              {sender.displayName}
                            </Typography>
                          </ActionMessageSenderNameStyled>
                          <ActionMessageSentDateStyled size="xxs">
                            {format(new Date(sentDate), 'p, MMM d, yyyy')}
                          </ActionMessageSentDateStyled>
                        </ActionMessageHeaderStyled>
                      )}
                      <MessageContentStyled
                        isActionMessageContent={isActionMessage}
                        isLoggedInUser={isLoggedInUser}
                        hasThumbnail={hasThumbnail}
                      >
                        {!isActionMessage ? (
                          <>
                            <FlexContainer align="start">
                              <MessageSenderNameStyled>
                                {isScheduleImportMessage && (
                                  <Typography variant="body3" color="red" isInline={true}>
                                    [Import]&nbsp;
                                  </Typography>
                                )}
                                {!isLoggedInUser && (
                                  <Typography
                                    variant="body2"
                                    isInline={true}
                                    color={theme.mode === 'dark' ? 'light' : undefined}
                                  >
                                    {sender.displayName}
                                  </Typography>
                                )}
                              </MessageSenderNameStyled>
                              <MessageSentDateStyled size="xs">
                                {format(new Date(sentDate), 'p, MMM d, yyyy')}
                              </MessageSentDateStyled>
                            </FlexContainer>
                            <MessageStyled
                              size="lg"
                              isText={isTextMessage}
                              isLoggedInUser={isLoggedInUser}
                            >
                              {text}
                              {!isAssigneeChangeTypeMessage && (
                                <AttachmentNameStyled>
                                  {action?.involvedUser?.displayName}
                                </AttachmentNameStyled>
                              )}
                            </MessageStyled>
                          </>
                        ) : (
                          <>
                            <FlexContainer
                              align="center"
                              direction={hasThumbnail ? 'column' : 'row'}
                            >
                              {isAttachmentTypeMessage ? (
                                <ActionMessageStyled
                                  size="lg"
                                  hasThumbnail={hasThumbnail}
                                  isLoggedInUser={isLoggedInUser}
                                >
                                  {typeId !== MessageTypesE.ScreenshotUpload ? freeText : null}
                                  {attachmentUrl && (
                                    <img
                                      className="image-preview"
                                      src={attachmentUrl}
                                      alt={attachmentName}
                                      onClick={(): void => handleUrlPreviewClick(attachmentUrl)}
                                      style={{ maxHeight: '120px', maxWidth: '100%' }}
                                    />
                                  )}
                                  <AttachmentNameStyled>{attachmentName}</AttachmentNameStyled>
                                  {attachmentText}
                                </ActionMessageStyled>
                              ) : (
                                <>
                                  <ActionMessageStyled size="lg">
                                    {freeText}&nbsp;
                                    {!isAssigneeChangeTypeMessage && (
                                      <AttachmentNameStyled>
                                        {action?.involvedUser?.displayName}
                                      </AttachmentNameStyled>
                                    )}
                                  </ActionMessageStyled>
                                  {isPrivateMsgShown && (
                                    <Typography color="darkBlue">
                                      &nbsp;Private: {action.privateMessage}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </FlexContainer>
                            {isGetApprovalTypeMessage && (
                              <ActionMessageStyled size="lg">
                                <AttachmentNameStyled>{action.privateMessage}</AttachmentNameStyled>
                              </ActionMessageStyled>
                            )}
                          </>
                        )}
                      </MessageContentStyled>
                    </FlexContainerStyled>
                  </MessageWrapStyled>
                );
              }
            )
          )}

          {unsavedMessage && (
            <MessageWrapStyled data-testid="unsaved-message" justify="end" align="start">
              <FlexContainer direction="column" align="end">
                <MessageContentStyled isLoggedInUser>
                  <FlexContainer justify="end">
                    <MessageSentDateStyled size="xs">
                      {format(unsavedMessage.sentDate, 'p, MMM d, yyyy')}
                    </MessageSentDateStyled>
                  </FlexContainer>
                  {!(isCreatingMessage && unsavedMessage.file) ? (
                    <>
                      {unsavedMessage.file ? (
                        <UnsavedScreenshotThumbnail file={unsavedMessage.file} />
                      ) : null}
                      <MessageStyled size="lg" isText isLoggedInUser>
                        {getUnsavedMessagePreviewText(unsavedMessage.text, unsavedMessage.file)}
                      </MessageStyled>
                    </>
                  ) : null}
                </MessageContentStyled>
                <FlexContainer
                  direction={isMobile ? 'column-reverse' : 'row'}
                  align="center"
                  justify="end"
                >
                  {!isCreatingMessage ? (
                    <CountdownStyled size="xs">{undoMessageTimeLeft} sec</CountdownStyled>
                  ) : (
                    <Box sx={{ minWidth: 48 }} />
                  )}
                  {isCreatingMessage ? (
                    <PendingSendStatusBar
                      label={getPendingSendStatusLabel(Boolean(unsavedMessage.file))}
                    />
                  ) : (
                    <ButtonGroup
                      sx={{ borderRadius: 10, padding: '0.5rem 1rem', height: 45 }}
                      size="small"
                    >
                      <Button
                        title="Undo message"
                        aria-label="Undo message"
                        onClick={handleUndoMessage}
                        sx={{ borderRadius: 50, padding: '0.5rem 1rem', width: 50 }}
                        size="small"
                      >
                        Undo
                      </Button>
                      <Button
                        title={getConfirmBarButtonTitle(
                          isCreatingMessage,
                          unsavedMessage.text,
                          unsavedMessage.file
                        )}
                        aria-label={getConfirmBarButtonAriaLabel(
                          isCreatingMessage,
                          unsavedMessage.text,
                          unsavedMessage.file
                        )}
                        onClick={(): void => handleConfirmMessage(assignToWarrCloud)}
                        sx={{ borderRadius: 10, padding: '0.5rem 1rem', minWidth: 75 }}
                        size="small"
                      >
                        Confirm
                      </Button>
                    </ButtonGroup>
                  )}
                </FlexContainer>
              </FlexContainer>
            </MessageWrapStyled>
          )}
        </MessagesWrapStyled>
      </MessagesScrollAreaStyled>

      <MessagesCardBottomStyled>
        <MessageOptionsWrapStyled align="center" justify="end">
          <FormControlLabel
            control={
              <Switch
                checked={assignToWarrCloud}
                onChange={handleAssignToWarrCloudChange}
                color="primary"
              />
            }
            label="Assign to Dealer"
            sx={{ mr: 0.5 }}
          />
        </MessageOptionsWrapStyled>

        <MessageForm
          requiresAttachment={requiresAttachment}
          roofTopName={roofTopName}
          assignToWarrCloud={assignToWarrCloud}
          handleOpenAddAttachmentPopup={handleOpenAddAttachmentPopup}
        />
      </MessagesCardBottomStyled>
    </MessagesCardInnerStyled>
  );
};

export default Messages;
